import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Check for returnTo parameter and session expiration
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const returnTo = params.get('returnTo');

    // If there's a returnTo parameter, it means session expired
    if (returnTo) {
      setSessionExpired(true);
    }
  }, [location]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Check for returnTo in URL params or localStorage
      const params = new URLSearchParams(location.search);
      const returnTo = params.get('returnTo') || localStorage.getItem('returnTo');

      // Clear returnTo from localStorage
      localStorage.removeItem('returnTo');

      // Redirect to saved location or default based on role
      if (returnTo && returnTo !== '/login') {
        navigate(returnTo, { replace: true });
      } else {
        // Redirect based on role - managers to dashboard, agents to assigned loans
        const isManager = localStorage.getItem('isCollectionsManager') === 'true';
        navigate(isManager ? '/dashboard' : '/assigned-loans', { replace: true });
      }
    }
  }, [isAuthenticated, navigate, location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_HOST_AUTH}/api/1.0.0/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      const result = await response.json();

      if (response.ok && result.user) {
        const user = result.user;

        // Store authentication data
        localStorage.setItem('accessToken', user.accessToken);
        localStorage.setItem('chatUserToken', user.chatUserToken || '');
        localStorage.setItem('userId', user._id);
        localStorage.setItem('userPermissions', ((user.permissions || {}).permissionsList || []).join(','));
        localStorage.setItem('countryCodes', JSON.stringify(user.countryCodes || ['TT']));
        localStorage.setItem('shardId', (user.countryCodes || ['TT'])[0]);

        // Store user info
        localStorage.setItem('userFirstName', user.firstName || '');
        localStorage.setItem('userLastName', user.lastName || '');
        localStorage.setItem('userEmail', user.email || '');

        // Store department info if available
        if (user.departmentInfo) {
          localStorage.setItem('userDepartment', JSON.stringify(user.departmentInfo));
          localStorage.setItem('isCollectionsManager', user.departmentInfo.isManager ? 'true' : 'false');
        } else {
          // Fallback: check if user has managerId or is in roles.managers
          const isManager = !user.managerId; // If no managerId, they are a manager
          localStorage.setItem('isCollectionsManager', isManager ? 'true' : 'false');
        }

        // Verify user has access to Collections
        // Check if user is in Collections department, Collections Officer department, or is a super admin
        const deptName = user.departmentInfo && user.departmentInfo.departmentName;
        const hasCollectionsAccess = deptName === 'Collections' || deptName === 'Collections Officer' ||
                                     user.isSuperAdmin;

        if (!hasCollectionsAccess) {
          setError('You do not have access to the Collections application. Please contact your administrator.');
          setLoading(false);
          return;
        }

        // Update AuthContext with user data
        login(user);

        // Redirect based on role - managers to dashboard, agents to assigned loans
        const isManager = (user.departmentInfo && user.departmentInfo.isManager) || !user.managerId;
        navigate(isManager ? '/dashboard' : '/assigned-loans');
      } else {
        setError(result.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred during login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-brand-section">
        <div className="brand-background">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
        
        <div className="brand-content">
          <div className="logo-container">
            <div className="logo-icon">
              <div className="icon-bar"></div>
              <div className="icon-bar short"></div>
              <div className="icon-bar"></div>
            </div>
            <h1>Collections</h1>
          </div>
          <p className="brand-tagline">
            Empowering your financial decisions with <br />
            <strong>Bayshore Financial Services</strong>
          </p>
        </div>
      </div>

      <div className="login-form-section">
        <div className="form-wrapper">
          <div className="form-header">
            <h2>Welcome Back</h2>
            <p>Please enter your details to sign in.</p>
          </div>

          {sessionExpired && !error && (
            <div className="error-alert" style={{ backgroundColor: '#fef3c7', borderColor: '#fbbf24', color: '#92400e' }}>
              <span className="error-icon" style={{ backgroundColor: '#f59e0b' }}>!</span>
              Your session has expired. Please login again to continue.
            </div>
          )}

          {error && (
            <div className="error-alert">
              <span className="error-icon">!</span>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@company.com"
                  disabled={loading}
                  required
                />
                <span className="input-focus-border"></span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  disabled={loading}
                  required
                />
                <span className="input-focus-border"></span>
              </div>
            </div>

            <div className="form-actions">
              <a href="#" className="forgot-password">Forgot password?</a>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? (
                <span className="loading-spinner"></span>
              ) : (
                <>
                  Sign In
                  <svg className="arrow-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                </>
              )}
            </button>
          </form>
          
          <div className="form-footer">
            <p>© {new Date().getFullYear()} Bayshore Financial Services</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
